import { Container, SceneObject, Sound, SoundSceneObject, SoundState } from '@cgs/wge';
import { RESOURCES_COMPONENT_TOKEN } from '../../type_definitions';
import { ResourcesComponent } from './resources_component';

export const CGS_SOUND_DEBUG_COMPONENT_TOKEN = Symbol('CGS_SOUND_DEBUG_COMPONENT_TOKEN');

interface SoundMeta {
  label: string;
  file: string;
}

interface LogEvent {
  t: number; // мс от старта записи
  action: 'play' | 'stop' | 'active';
  label: string;
  file: string;
  from: string;
  soundIdx: number; // индекс в this.sounds для управления (пауза/стоп)
}

// Запись одного звука для вкладки «Ресурсы» (всё, что сбилджено и есть в ресурсах).
interface ResourceEntry {
  sound: Sound;
  label: string;
  file: string;
}

const MAX_EVENTS = 1000;

type Tab = 'rec' | 'res';

// Универсальный дебаг-компонент звуков (только при включённых читах). Тоггл панели — Shift+M.
// Вкладка «Запись»: пока идёт запись, в лог попадают только реально сыгравшие/остановленные
// звуки, с указанием id-ноды, файла и места вызова play/stop — чтобы поймать последовательность
// (например один спин) и увидеть, где что игралось.
// Вкладка «Ресурсы»: все звуки, сбилдженные в ресурсах игры — можно проиграть и послушать.
// Pause all / Resume paused — пауза/возобновление всех играющих звуков сразу.
export class CGSSoundDebugComponent {
  private static activeRecorder: CGSSoundDebugComponent | null = null;
  // Все звуки, по которым хоть раз звали play() — чтобы на старте записи увидеть уже играющие.
  private static readonly allSounds = new Set<Sound>();

  private readonly sceneRoot: SceneObject;
  private readonly metaBySound = new Map<Sound, SoundMeta>();
  private readonly resources: ResourceEntry[] = [];

  private recording = false;
  private startTime = 0;
  private events: LogEvent[] = [];
  private readonly sounds: Sound[] = [];
  private readonly idxBySound = new Map<Sound, number>();

  // Звуки, поставленные на паузу через «Pause all» — чтобы «Resume» вернул только их.
  private pausedByAll: Sound[] = [];

  private tab: Tab = 'rec';
  private resourceFilter = '';

  private rootEl: HTMLDivElement | null = null;
  private recTabBtn: HTMLButtonElement | null = null;
  private resTabBtn: HTMLButtonElement | null = null;
  private recToolbarEl: HTMLDivElement | null = null;
  private resToolbarEl: HTMLDivElement | null = null;
  private listEl: HTMLDivElement | null = null;
  private resListEl: HTMLDivElement | null = null;
  private resCountEl: HTMLSpanElement | null = null;
  private recordBtn: HTMLButtonElement | null = null;
  private statusEl: HTMLSpanElement | null = null;
  private visible = false;

  constructor(container: Container) {
    // Всё дерево сцены, а не только контейнер sounds: так в список попадают и звуки,
    // вшитые в сцены иконок/бэкграунда (например icons/sp_freespins_triggered.ogg).
    this.sceneRoot = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;

    this.patchSoundPrototype();
    this.rescan();
    this.buildOverlay();

    document.addEventListener('keydown', (e) => {
      if (e.shiftKey && e.code === 'KeyM') {
        this.toggle();
      }
    });

     
    console.log('[SoundDebug] Shift+M — панель звуков (запись / ресурсы / pause all)');
  }

  private toggle(): void {
    this.visible = !this.visible;
    if (this.rootEl) {
      this.rootEl.style.display = this.visible ? 'flex' : 'none';
    }
    if (this.visible) {
      this.render();
    }
  }

  // --- запись ---

  private startRecording(): void {
    this.recording = true;
    this.startTime = performance.now();
    CGSSoundDebugComponent.activeRecorder = this;
    this.snapshotActive();
    this.refreshControls();
    this.render();
  }

  // Сразу заносим в лог звуки, которые УЖЕ играют на момент старта записи
  // (их play() был вызван раньше, поэтому в события они бы не попали).
  private snapshotActive(): void {
    const candidates = new Set<Sound>([
      ...this.metaBySound.keys(),
      ...CGSSoundDebugComponent.allSounds,
    ]);
    for (const sound of candidates) {
      if (sound.state !== SoundState.Playing || sound.volume <= 0.001) {
        continue;
      }
      const meta = this.metaBySound.get(sound);
      const file = meta?.file ?? this.fileOf(sound);
      this.events.push({
        t: 0,
        action: 'active',
        label: meta?.label ?? file,
        file,
        from: '(играл до записи)',
        soundIdx: this.idxFor(sound),
      });
    }
  }

  private idxFor(sound: Sound): number {
    let idx = this.idxBySound.get(sound);
    if (idx === undefined) {
      idx = this.sounds.push(sound) - 1;
      this.idxBySound.set(sound, idx);
    }
    return idx;
  }

  private stopRecording(): void {
    this.recording = false;
    CGSSoundDebugComponent.activeRecorder = null;
    this.refreshControls();
    this.render();
  }

  private clear(): void {
    this.events = [];
    this.render();
  }

  // --- pause all / resume paused ---

  private pauseAll(): void {
    this.pausedByAll = [];
    const candidates = new Set<Sound>([
      ...this.metaBySound.keys(),
      ...CGSSoundDebugComponent.allSounds,
    ]);
    for (const sound of candidates) {
      if (sound.state === SoundState.Playing) {
        sound.pause();
        this.pausedByAll.push(sound);
      }
    }
    this.render();
  }

  private resumePaused(): void {
    for (const sound of this.pausedByAll) {
      sound.resume();
    }
    this.pausedByAll = [];
    this.render();
  }

  // Вызывается из пропатченных Sound.play/stop (см. patchSoundPrototype).
  private capture(sound: Sound, action: 'play' | 'stop', from: string): void {
    if (!this.recording) {
      return;
    }
    const meta = this.metaBySound.get(sound);
    const file = meta?.file ?? this.fileOf(sound);
    const label = meta?.label ?? file;

    this.events.push({
      t: performance.now() - this.startTime,
      action,
      label,
      file,
      from,
      soundIdx: this.idxFor(sound),
    });
    if (this.events.length > MAX_EVENTS) {
      this.events.shift();
    }
    if (this.visible && this.tab === 'rec') {
      this.render();
    }
  }

  private fileOf(sound: Sound): string {
    return (sound as unknown as { _resource?: { id?: string } })._resource?.id ?? '?';
  }

  // --- патч прототипа Sound: ловим play/stop и место вызова (стек снимаем только при записи) ---

  private patchSoundPrototype(): void {
    const proto = Sound.prototype as unknown as {
      play: () => void;
      stop: () => void;
      __dbgPatched?: boolean;
    };
    if (proto.__dbgPatched) {
      return;
    }
    proto.__dbgPatched = true;

    const wrap = (method: 'play' | 'stop'): void => {
      const original = proto[method];
      proto[method] = function (this: Sound): void {
        if (method === 'play') {
          CGSSoundDebugComponent.allSounds.add(this);
        }
        const rec = CGSSoundDebugComponent.activeRecorder;
        if (rec) {
          rec.capture(this, method, CGSSoundDebugComponent.callSite());
        }
        return (original as () => void).call(this);
      };
    };

    wrap('play');
    wrap('stop');
  }

  private static callSite(): string {
    const stack = new Error().stack;
    if (!stack) {
      return '';
    }
    const lines = stack.split('\n').slice(1);
    const frame = lines.find(
      (l) =>
        !l.includes('callSite') &&
        !l.includes('CGSSoundDebugComponent') &&
        !/\.(play|stop) /.test(l) &&
        !/Sound\.(play|stop)/.test(l)
    );
    if (!frame) {
      return '';
    }
    return frame
      .trim()
      .replace(/^at\s+/, '')
      .replace(/.*\/(.*\.ts):(\d+):\d+.*/, '$1:$2')
      .replace(/\s*\(.*\)$/, '')
      .slice(0, 90);
  }

  // --- сопоставление Sound -> id-нода/файл ---

  // Пересканировать дерево сцены. Идемпотентно — зовётся и на старте, и при открытии
  // вкладки «Ресурсы» (иконки кладутся на барабаны по ходу игры, их вшитые звуки
  // появляются в дереве позже).
  private rescan(): void {
    this.buildSoundMeta();
    this.buildResourceList();
  }

  private buildSoundMeta(): void {
    const objects = this.sceneRoot.findAllByType(SoundSceneObject) as SoundSceneObject[];
    for (const sso of objects) {
      const sound = (sso as unknown as { _sound: Sound })._sound;
      if (!sound || this.metaBySound.has(sound)) {
        continue;
      }
      this.metaBySound.set(sound, { label: this.labelFor(sso), file: this.fileOf(sound) });
    }
  }

  // Все сбилдженные звуки — для вкладки «Ресурсы». Источники: дерево сцены (metaBySound)
  // + все звуки, которые хоть раз играли (allSounds) — так ловим даже пуловые/удалённые
  // из дерева. Дедуп по Sound, список перестраивается с нуля.
  private buildResourceList(): void {
    this.resources.length = 0;
    const seen = new Set<Sound>();
    const add = (sound: Sound, label: string, file: string): void => {
      if (seen.has(sound)) {
        return;
      }
      seen.add(sound);
      this.resources.push({ sound, label, file });
    };
    for (const [sound, meta] of this.metaBySound) {
      add(sound, meta.label, meta.file);
    }
    for (const sound of CGSSoundDebugComponent.allSounds) {
      add(sound, this.fileOf(sound), this.fileOf(sound));
    }
    this.resources.sort((a, b) => a.label.localeCompare(b.label));
  }

  private labelFor(sso: SceneObject): string {
    let node: SceneObject | null = sso;
    while (node) {
      if (node.id) {
        return node.id;
      }
      node = node.parent;
    }
    return '(no id)';
  }

  // --- UI ---

  private buildOverlay(): void {
    this.rootEl = document.createElement('div');
    this.rootEl.style.cssText = [
      'position:fixed',
      'top:20px',
      'right:20px',
      'width:460px',
      'max-height:82vh',
      'display:none',
      'flex-direction:column',
      'background:rgba(15,15,20,0.94)',
      'color:#e6e6e6',
      'font:11px/1.45 monospace',
      'border:1px solid #444',
      'border-radius:6px',
      'z-index:2147483647',
      'pointer-events:auto',
    ].join(';');

    // Шапка: заголовок + вкладки + глобальные pause all / resume.
    const header = document.createElement('div');
    header.style.cssText =
      'display:flex;align-items:center;gap:6px;padding:8px;border-bottom:1px solid #333;flex-wrap:wrap';

    const title = document.createElement('span');
    title.style.cssText = 'font-weight:bold;color:#7fd;flex:0 0 auto';
    title.textContent = '🔊';

    this.recTabBtn = this.makeButton('Запись', () => this.switchTab('rec'));
    this.resTabBtn = this.makeButton('Ресурсы', () => this.switchTab('res'));

    const pauseAllBtn = this.makeButton('⏸ All', () => this.pauseAll());
    pauseAllBtn.title = 'Пауза всех играющих звуков';
    const resumeBtn = this.makeButton('▶ All', () => this.resumePaused());
    resumeBtn.title = 'Возобновить поставленные на паузу';

    header.append(title, this.recTabBtn, this.resTabBtn, pauseAllBtn, resumeBtn);

    // Тулбар вкладки «Запись».
    this.recToolbarEl = document.createElement('div');
    this.recToolbarEl.style.cssText =
      'display:flex;align-items:center;gap:8px;padding:8px;border-bottom:1px solid #333';

    this.recordBtn = this.makeButton('● Запись', () =>
      this.recording ? this.stopRecording() : this.startRecording()
    );
    const clearBtn = this.makeButton('Очистить', () => this.clear());

    this.statusEl = document.createElement('span');
    this.statusEl.style.cssText = 'margin-left:auto;color:#888';

    this.recToolbarEl.append(this.recordBtn, clearBtn, this.statusEl);

    // Тулбар вкладки «Ресурсы»: фильтр.
    this.resToolbarEl = document.createElement('div');
    this.resToolbarEl.style.cssText =
      'display:none;align-items:center;gap:8px;padding:8px;border-bottom:1px solid #333';

    const filterInput = document.createElement('input');
    filterInput.type = 'text';
    filterInput.placeholder = 'фильтр…';
    filterInput.style.cssText =
      'flex:1;background:#1a1a22;color:#e6e6e6;border:1px solid #555;border-radius:4px;' +
      'padding:3px 6px;font:11px monospace';
    filterInput.addEventListener('input', () => {
      this.resourceFilter = filterInput.value.trim().toLowerCase();
      this.renderResources();
    });
    this.resCountEl = document.createElement('span');
    this.resCountEl.style.cssText = 'color:#888';
    this.resToolbarEl.append(filterInput, this.resCountEl);

    // Списки.
    this.listEl = document.createElement('div');
    this.listEl.style.cssText = 'overflow-y:auto;padding:8px';
    this.listEl.addEventListener('click', (e) => this.handleSoundClick(e));

    this.resListEl = document.createElement('div');
    this.resListEl.style.cssText = 'display:none;overflow-y:auto;padding:8px';
    this.resListEl.addEventListener('click', (e) => this.handleSoundClick(e));

    this.rootEl.append(header, this.recToolbarEl, this.resToolbarEl, this.listEl, this.resListEl);
    document.body.appendChild(this.rootEl);

    this.refreshTabs();
    this.refreshControls();
  }

  private switchTab(tab: Tab): void {
    this.tab = tab;
    if (tab === 'res') {
      this.rescan();
    }
    this.refreshTabs();
    this.render();
  }

  private refreshTabs(): void {
    const active = '#2a3a44';
    const inactive = '#2a2a33';
    if (this.recTabBtn) {
      this.recTabBtn.style.background = this.tab === 'rec' ? active : inactive;
    }
    if (this.resTabBtn) {
      this.resTabBtn.style.background = this.tab === 'res' ? active : inactive;
    }
    const recVisible = this.tab === 'rec';
    if (this.recToolbarEl) {
      this.recToolbarEl.style.display = recVisible ? 'flex' : 'none';
    }
    if (this.resToolbarEl) {
      this.resToolbarEl.style.display = recVisible ? 'none' : 'flex';
    }
    if (this.listEl) {
      this.listEl.style.display = recVisible ? 'block' : 'none';
    }
    if (this.resListEl) {
      this.resListEl.style.display = recVisible ? 'none' : 'block';
    }
  }

  // Делегированный клик по кнопкам управления звуком (общий для обеих вкладок).
  private handleSoundClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const act = target.dataset?.act;
    const idx = target.dataset?.idx;
    if (!act || idx === undefined) {
      return;
    }
    const sound = this.sounds[Number(idx)];
    if (!sound) {
      return;
    }
    if (act === 'play') {
      sound.play();
    } else if (act === 'pause') {
      sound.pause();
    } else if (act === 'resume') {
      sound.resume();
    } else if (act === 'stop') {
      sound.stop();
    }
    if (this.tab === 'res') {
      this.renderResources();
    }
  }

  private makeButton(text: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.style.cssText =
      'background:#2a2a33;color:#e6e6e6;border:1px solid #555;border-radius:4px;' +
      'padding:3px 8px;cursor:pointer;font:11px monospace';
    btn.addEventListener('click', onClick);
    return btn;
  }

  private refreshControls(): void {
    if (this.recordBtn) {
      this.recordBtn.textContent = this.recording ? '■ Стоп' : '● Запись';
      this.recordBtn.style.color = this.recording ? '#f66' : '#e6e6e6';
    }
    if (this.statusEl) {
      this.statusEl.textContent = this.recording ? '● rec…' : `${this.events.length} событий`;
      this.statusEl.style.color = this.recording ? '#f66' : '#888';
    }
  }

  private render(): void {
    if (this.tab === 'rec') {
      this.renderEvents();
    } else {
      this.renderResources();
    }
  }

  private renderEvents(): void {
    if (!this.listEl) {
      return;
    }
    this.refreshControls();

    if (this.events.length === 0) {
      this.listEl.innerHTML =
        '<div style="color:#888">Нажми «● Запись», проиграй нужный момент, затем «■ Стоп».</div>';
      return;
    }

    // Новые сверху.
    this.listEl.innerHTML = this.events
      .slice()
      .reverse()
      .map((ev) => {
        const icon = ev.action === 'play' ? '▶' : ev.action === 'stop' ? '■' : '🔊';
        const color = ev.action === 'play' ? '#7f7' : ev.action === 'stop' ? '#c88' : '#fd7';
        const time = (ev.t / 1000).toFixed(2).padStart(6, ' ');
        return (
          `<div style="margin-bottom:3px">` +
          `<span style="color:#666">${time}s</span> ` +
          `<span style="color:${color}">${icon} ${esc(ev.label)}</span> ` +
          ctrlBtn(ev.soundIdx, 'pause', '⏸') +
          ctrlBtn(ev.soundIdx, 'resume', '▶') +
          ctrlBtn(ev.soundIdx, 'stop', '⏹') +
          `<div style="color:#777;padding-left:20px">${esc(ev.file)}</div>` +
          (ev.from ? `<div style="color:#9ad;padding-left:20px">↳ ${esc(ev.from)}</div>` : '') +
          `</div>`
        );
      })
      .join('');
  }

  private renderResources(): void {
    if (!this.resListEl) {
      return;
    }
    if (this.resCountEl) {
      this.resCountEl.textContent = `${this.resources.length} звуков`;
    }
    const filter = this.resourceFilter;
    const shown = filter
      ? this.resources.filter(
          (r) => r.label.toLowerCase().includes(filter) || r.file.toLowerCase().includes(filter)
        )
      : this.resources;

    if (shown.length === 0) {
      this.resListEl.innerHTML = '<div style="color:#888">Ничего не найдено.</div>';
      return;
    }

    this.resListEl.innerHTML = shown
      .map((r) => {
        const idx = this.idxFor(r.sound);
        const playing = r.sound.state === SoundState.Playing;
        const labelColor = playing ? '#7f7' : '#e6e6e6';
        return (
          `<div style="margin-bottom:3px">` +
          `<span style="color:${labelColor}">${playing ? '▶ ' : ''}${esc(r.label)}</span> ` +
          ctrlBtn(idx, 'play', '▶') +
          ctrlBtn(idx, 'pause', '⏸') +
          ctrlBtn(idx, 'stop', '⏹') +
          `<div style="color:#777;padding-left:20px">${esc(r.file)}</div>` +
          `</div>`
        );
      })
      .join('');
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Инлайновая кнопка управления звуком (обрабатывается делегированием на списках).
function ctrlBtn(idx: number, act: 'play' | 'pause' | 'resume' | 'stop', glyph: string): string {
  return (
    `<button data-act="${act}" data-idx="${idx}" title="${act}" ` +
    `style="background:#2a2a33;color:#ccc;border:1px solid #555;border-radius:3px;` +
    `padding:0 5px;margin-right:2px;cursor:pointer;font:11px monospace">${glyph}</button>`
  );
}
